// REMOVE_AFTER_QUOTE_V2
import {
  CollaborationAccessLevelEnum,
  IQuoteRequestSchema,
  IQuoteRespSchema,
  IQuoteReviewReq,
  QuoteRequestSchema,
  QuoteStatusEnum,
  QuoteTypeEnum,
  getQuoteRequestSchema,
} from '@monetize/types/app';
import { useQueryClient } from '@tanstack/react-query';
import { formatISO } from 'date-fns/formatISO';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { handleApiErrorToast } from '../../../api/axios';
import { useChangeQuoteOwner } from '../../../api/cpqService';
import { notesServiceQueryKeys } from '../../../api/queryKeysService';
import { useGetById } from '../../../api/queryUtils';
import { MPageLoader } from '../../../components/Monetize';
import { ROUTES } from '../../../constants';
import {
  INITIAL_FORM_VALUES,
  QUOTE_EDITABLE_STATUSES,
} from '../../../constants/quotes';
import { useProductOfferings, useQuote } from '../../../hooks';
import {
  useQuoteDocumentUploadNavigationGuard,
  useQuoteDocumentUploadTabCloseGuard,
} from '../../../hooks/useQuoteDocumentUploads';
import { useACL } from '../../../services/acl/acl';
import { getQuoteRequestFromQuoteResponse } from '../../../utils/quotes';
import { QuotePage } from '../QuoteV2/QuotePage';
import { useQuoteState } from './quoteContext';
import { QuoteContextProvider } from './QuoteContextProvider';
import QuoteReviewModal, { useQuoteReviewModalData } from './QuoteReviewModal';
import { updateQuoteQueryCache } from './quoteUtils';

/**
 * Wrapper for Quote pages (Review and Edit)
 *
 * 1. Ensures the quote exists and is accessible, otherwise redirects to the quotes list
 * 2. Renders the quote edit or review page based on the quote status
 * 3. Based on permissions, may render the quote in read-only mode
 *
 */
export const QuotePageWrapperContainer = () => {
  const { quoteId = '' } = useParams();
  const location = useLocation();
  return (
    <QuotePageWrapper key={`${quoteId}-${location.key}`} quoteId={quoteId} />
  );
};

const QuotePageWrapper = ({ quoteId }: { quoteId: string }) => {
  // Live here (rather than inside QuoteDocumentsList) so they keep guarding
  // across the Internal/External view toggle, which unmounts the document
  // list along with everything else in QuotePageInternal.
  useQuoteDocumentUploadNavigationGuard(quoteId);
  useQuoteDocumentUploadTabCloseGuard(quoteId);

  const queryClient = useQueryClient();
  const [isInternalView, setIsInternalView] = useState<boolean>(true);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(true); // isReadOnlyQuote || (!isDraft && !isAcceptedAdminEditable)
  const [isReadOnlyQuote, setIsReadOnlyQuote] = useState<boolean>(true); // When user does not have permission to edit quote or quote accessLevel is READ

  const navigate = useNavigate();
  const { canDo } = useACL();
  const canUpdateSales = canDo([['sales', 'update']]);

  const { data: quote, isError } = useGetById<IQuoteRespSchema>(
    'cpqServiceQuotes',
    quoteId,
    {
      enabled: !!quoteId,
      retry: false,
      refetchOnWindowFocus: true,
      meta: { showErrorToast: true },
    },
  );

  // UseQuote hook for handling quote api calls
  const quoteData = useQuote(quote);

  const {
    updateQuote,
    createQuote,
    transitionQuoteStatus,
    setQuote,
    loading: quoteLoading,
    billingScheduleLoading,
  } = quoteData;

  useEffect(() => {
    if (quote) {
      // only update the quote if it was modified
      // This shouldn't be needed, but the quote from useGetById was being called in an infinite loop
      // likely related to the `select` property which was removed, so this is just a safeguard and may not actually be needed
      setQuote((previousQuote) => {
        if (!previousQuote || previousQuote.modifyDate !== quote.modifyDate) {
          return quote;
        }
        return previousQuote;
      });
    } else if (isError) {
      navigate(ROUTES.SALES_QUOTES_ROUTE, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, isError]);

  const changeOwnerData = useChangeQuoteOwner({
    onSuccess: (data) => {
      setQuote(data);
    },
  });
  const { isPending: changeQuoteOwnerLoading } = changeOwnerData;

  // useProductOfferings hook for handling product catalog offerings api calls
  const productOfferingsData = useProductOfferings(
    quote?.currency,
    quote?.accountId,
    quote?.type === QuoteTypeEnum.AMENDMENT
      ? quote.contractAmendmentDate
      : quote?.contractStartDate,
  );

  const {
    loading: productOfferingsLoading,
    productOfferings,
    productOfferingsObj,
  } = productOfferingsData;

  // useForm hook for handling form states
  const methods = useForm<IQuoteRequestSchema>({
    mode: 'onChange',
    resolver: getQuoteRequestSchema(quote),
    defaultValues: {
      contractStartDate: formatISO(new Date(), { representation: 'date' }),
      ...INITIAL_FORM_VALUES,
    },
    values: quote ? getQuoteRequestFromQuoteResponse(quote) : undefined,
    resetOptions: {
      keepDirtyValues: true,
      keepErrors: true,
    },
  });

  const {
    formState: { isSubmitting },
  } = methods;

  const isLoading =
    isSubmitting ||
    quoteLoading ||
    productOfferingsLoading ||
    billingScheduleLoading ||
    changeQuoteOwnerLoading;

  // Other Quote states
  const quoteStateData = useQuoteState({
    quote,
    productOfferingsObj,
    isLoading: quoteLoading,
  });

  // handles quote update/create logic on form submit
  const onSubmit = async (data: IQuoteRequestSchema) => {
    const payload = QuoteRequestSchema.parse(data);

    const response = quote?.id
      ? await updateQuote(quote.id, payload)
      : await createQuote(payload);
    if (response) {
      updateQuoteQueryCache(queryClient, response);
    }

    return response;
  };

  const handleReview = async (extraData?: IQuoteReviewReq) => {
    // Avoid review from being called twice - in some rare cases this was happening and not sure from where
    if (
      !quote?.id ||
      quote.status !== QuoteStatusEnum.DRAFT ||
      quoteStateData.isReviewBtnPressed
    ) {
      return;
    }

    quoteStateData.setIsReviewBtnPressed(true);
    // Add delay in submission to ensure that any other updates have time to start before this starts
    // transitioning status will wait for other actions to complete, but only if they are started first
    setTimeout(async () => {
      try {
        const quoteResponse = await transitionQuoteStatus(quote.id, {
          newState: QuoteStatusEnum.REVIEW,
          extraData,
        });
        if (quoteResponse) {
          setQuote(quoteResponse);
          setIsInternalView(false);
          queryClient.invalidateQueries({
            queryKey: [
              ...notesServiceQueryKeys.noteListById('quotes', quoteResponse.id),
            ],
          });
        }
      } catch (error) {
        handleApiErrorToast(error);
      } finally {
        quoteStateData.setIsReviewBtnPressed(false);
      }
    }, 500);
  };

  // Review Quote Modal data
  const reviewQuoteModalData = useQuoteReviewModalData({
    handleReview,
    quoteFirstApprovalUser: quoteData.quoteFirstApprovalUser,
  });

  /**
   * Ensures that the URL is correct based on the state of the quote
   * This usually only changes if the user visits the page directly and the quote changed states
   */
  useEffect(() => {
    // This should not happen unless there is a developer error, adding here as a fallback
    if (!quoteId) {
      handleApiErrorToast(new Error('A quote id is required'));
      navigate(ROUTES.SALES_QUOTES_ROUTE, { replace: true });
    }
  }, [quoteId]);

  useLayoutEffect(() => {
    if (quote?.accessLevel && quote?.status) {
      const isReadOnlyAccess =
        !canUpdateSales ||
        quote.accessLevel === CollaborationAccessLevelEnum.READ;
      const isEditable = QUOTE_EDITABLE_STATUSES.has(quote.status);

      setIsReadOnlyQuote(isReadOnlyAccess);
      setIsReadOnly(isReadOnlyAccess || !isEditable);

      const nextIsInternalView = !isReadOnlyAccess && isEditable;
      setIsInternalView((prevIsInternalView) =>
        prevIsInternalView === nextIsInternalView
          ? prevIsInternalView
          : nextIsInternalView,
      );
    }
  }, [quote?.accessLevel, quote?.status]);

  /**
   * quote?.id !== quoteData?.quote?.id is added to ensure that we don't have data from previous quote
   * spread around the form when the quote is changed
   */
  if (!quote || quoteId !== quote.id || quote?.id !== quoteData?.quote?.id) {
    return <MPageLoader />;
  }

  return (
    <>
      <QuoteContextProvider
        initialQuote={quote}
        isReadOnly={isReadOnly}
        isReadOnlyQuote={isReadOnlyQuote}
        isInternalView={isInternalView}
        quoteFormMethods={methods}
        quoteData={quoteData}
        reviewQuoteModalData={reviewQuoteModalData}
        productOfferingsData={productOfferingsData}
        changeOwnerData={changeOwnerData}
        quoteStateData={quoteStateData}
        isLoading={isLoading}
        onSubmit={onSubmit}
        handleReview={handleReview}
        setIsInternalView={setIsInternalView}
      >
        <QuotePage />
      </QuoteContextProvider>
      <QuoteReviewModal
        reviewQuoteModalData={reviewQuoteModalData}
        quoteData={quoteData}
        handleReview={handleReview}
      />
    </>
  );
};
